import { createPopper } from '@popperjs/core'
import Vue from 'vue'

// Global variable to track the currently open tooltip
let currentOpenTooltip = null

const TooltipPlugin = {
  install(Vue) {
    Vue.directive('tooltip', {
      bind(el, binding) {
        const tooltipText = binding.value.text || ''
        const options = {
          placement: binding.value.placement || 'top',
          backgroundColor: binding.value.backgroundColor || 'black',
          color: binding.value.color || 'white',
          width: binding.value.width || 'auto',
          height: binding.value.height || 'auto',
          trigger: binding.value.trigger || 'hover' // 'hover' or 'click'
        }

        const tooltipEl = document.createElement('div')
        tooltipEl.style.backgroundColor = options.backgroundColor
        tooltipEl.style.color = options.color
        tooltipEl.style.padding = '5px 10px'
        tooltipEl.style.borderRadius = '4px'
        tooltipEl.style.maxWidth = options.width
        tooltipEl.style.maxHeight = options.height
        tooltipEl.style.fontSize = '14px'
        tooltipEl.style.pointerEvents = 'none'
        tooltipEl.style.whiteSpace = 'nowrap'
        tooltipEl.style.zIndex = '1000'
        tooltipEl.style.display = 'none'
        tooltipEl.style.whiteSpace = 'normal'
        tooltipEl.style.textAlign = 'center'

        // Add scrollable content container
        if (options.height !== 'auto') {
          tooltipEl.style.overflowY = 'auto'
        }

        tooltipEl.innerHTML = tooltipText

        const arrow = document.createElement('div')
        arrow.className = 'arrow'
        arrow.style.width = '10px'
        arrow.style.height = '10px'
        arrow.style.position = 'absolute'
        arrow.style.backgroundColor = 'transparent'
        tooltipEl.appendChild(arrow)

        document.body.appendChild(tooltipEl)

        const popperInstance = createPopper(el, tooltipEl, {
          placement: options.placement,
          modifiers: [
            { name: 'arrow', options: { element: arrow } },
            { name: 'offset', options: { offset: [0, 8] } } // Adjusts distance
          ]
        })

        const showTooltip = () => {
          // Close any currently open tooltip
          if (currentOpenTooltip && currentOpenTooltip !== tooltipEl) {
            currentOpenTooltip.style.display = 'none'
          }

          tooltipEl.style.display = 'block'
          popperInstance.update()

          // Set this tooltip as the current open tooltip
          currentOpenTooltip = tooltipEl
        }

        const hideTooltip = () => {
          tooltipEl.style.display = 'none'
          // Clear the current open tooltip reference if this was the open one
          if (currentOpenTooltip === tooltipEl) {
            currentOpenTooltip = null
          }
        }

        // Helper function to check if element is a valid DOM element
        const isDOMElement = (element) => {
          return element && typeof element === 'object' && element.nodeType === 1
        }

        // Handle different trigger types
        if (options.trigger === 'click') {
          tooltipEl.style.pointerEvents = 'auto' // Allow interactions when click-triggered

          const handleClick = (event) => {
            // Check if el is a valid DOM element before using DOM methods
            if (!isDOMElement(el)) return

            // If clicking on the element itself and tooltip is hidden, show it
            if (el.contains(event.target) && tooltipEl.style.display === 'none') {
              event.stopPropagation()
              showTooltip()
              return
            }

            // If clicking outside the element or tooltip, hide it
            if (!el.contains(event.target) && !tooltipEl.contains(event.target)) {
              hideTooltip()
            }
          }

          // Add click listener to the element (with DOM element check)
          if (isDOMElement(el)) {
            el.addEventListener('click', (event) => {
              event.stopPropagation()
              if (tooltipEl.style.display === 'none') {
                showTooltip()
              } else {
                hideTooltip()
              }
            })
          }

          // Add document click listener to handle clicks outside
          document.addEventListener('click', handleClick)

          // Store the click handler for cleanup
          el._tooltipClickHandler = handleClick
        } else {
          // Default hover behavior (with DOM element check)
          if (isDOMElement(el)) {
            el.addEventListener('mouseenter', showTooltip)
            el.addEventListener('mouseleave', hideTooltip)
          }
        }

        el._tooltipEl = tooltipEl
        el._popperInstance = popperInstance
        el._tooltipOptions = options
      },
      unbind(el) {
        // Helper function to check if element is a valid DOM element
        const isDOMElement = (element) => {
          return element && typeof element === 'object' && element.nodeType === 1
        }

        if (isDOMElement(el) && el._tooltipEl) {
          // Clear the current open tooltip reference if this was the open one
          if (currentOpenTooltip === el._tooltipEl) {
            currentOpenTooltip = null
          }
          el._tooltipEl.remove()
        }
        if (isDOMElement(el) && el._popperInstance) {
          el._popperInstance.destroy()
        }
        if (el._tooltipClickHandler) {
          document.removeEventListener('click', el._tooltipClickHandler)
        }
      }
    })
  }
}

Vue.use(TooltipPlugin)
export default TooltipPlugin
